import Box from "@mui/material/Box";
import Icon from "@mui/material/Icon";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { alpha, useTheme } from "@mui/material/styles";
import type { FC, ReactNode } from "react";
import { tokens } from "../../theme/tokens";
import { APP_CONTENT_GRID } from "../appLayout";
import {
  CIP_NOTICE,
  CUB_TENANT_CONTENT,
  LABELS,
  PLATFORM_LINKS,
  PRIVACY_SENTENCE,
  lateChargesSentence,
} from "./content";
import {
  CubWordmark,
  InstagramGlyph,
  LinkedinGlyph,
  PoweredByCubBadge,
  WhatsappGlyph,
  YoutubeGlyph,
} from "./glyphs";
import { CUB_PARTNERS } from "./partners";
import type {
  AppFooterApp,
  AppFooterPartnerLogo,
  AppFooterProps,
} from "./types";

/* ── Medidas da spec (Figma `<footer>`, node 3894:13140) ─────────────────── */

/**
 * Raio do canto da arte dos logos de parceiro: 4px numa tela de 140px = 2,86%.
 * Em porcentagem, acompanha a caixa — ver `companyLogoRadius` nos tipos.
 */
const PARTNER_LOGO_ART_RADIUS = "2.86%";

const PAD_Y = "44px";
/** Gap entre colunas do desktop e entre blocos empilhados no mobile. */
const GAP_SECTION = "44px";
const GAP_COLUMN = "107px";
/** Gap interno de bloco (entre parágrafos, entre logo e redes). */
const GAP_BLOCK = "24px";
const GAP_LINE = "4px";
const GAP_SECURITY = "8px";
const INFO_COLUMN_WIDTH = "235px";
const TEXT_MIN_WIDTH = "343px";
const WORDMARK_WIDTH = 90;
const PARTNER_LOGO_SIZE = "90px";
const SOCIAL_GLYPH = 20;
const CONTACT_GLYPH = "16px";

/* ── Paleta por variante ─────────────────────────────────────────────────── */

interface Surface {
  /** Fundo do footer. */
  bg: string;
  /** Corpo de texto, links e ícones — de contato e de rede social. */
  body: string;
  /** Rótulos e títulos. */
  strong: string;
  /** Wordmark CUB — no billing-viewer a marca é azul, não preta. */
  wordmark: string;
  /** Régua divisória (só white label). */
  divider: string;
}

function resolveSurface(
  props: Pick<AppFooterProps, "app" | "tenant" | "partnerColors">,
): Surface {
  const { app, tenant = "cub", partnerColors } = props;

  // O billing-viewer é sempre grey200, nos dois tenants — o parceiro não pinta
  // a superfície do boleto.
  if (app === "billing-viewer") {
    // O parceiro pode trocar a superfície do boleto; no tenant CUB, não.
    const wl = tenant === "whitelabel" ? partnerColors : undefined;
    const fg = wl?.billingSurfaceContrast;
    return {
      bg: wl?.billingSurface ?? tokens.grey200,
      body: fg ?? tokens.textSecondary,
      strong: fg ?? tokens.textPrimary,
      // A marca CUB sobre superfície clara é azul; no white label o selo
      // "Tecnologia da" acompanha o contraste do parceiro.
      wordmark: tenant === "cub" ? tokens.blue : (fg ?? tokens.textPrimary),
      // A régua também precisa acompanhar: `grey400` desaparece em superfície
      // escura, e é o mesmo motivo do `alpha()` no ramo do statement.
      divider: fg ? alpha(fg, 0.32) : tokens.grey400,
    };
  }

  if (tenant === "cub") {
    return {
      bg: tokens.cobalt,
      body: tokens.info200,
      strong: tokens.white,
      wordmark: tokens.white,
      divider: alpha(tokens.white, 0.32),
    };
  }

  // White label + statement-viewer: superfície do parceiro. Fallback quando o
  // parceiro não tem cor secundária / contraste secundário configurados.
  const fg = partnerColors?.secondaryContrast ?? tokens.white;
  return {
    bg: partnerColors?.secondary ?? tokens.grey900,
    body: fg,
    strong: fg,
    wordmark: fg,
    divider: alpha(fg, 0.32),
  };
}

/* ── Primitivos de texto ─────────────────────────────────────────────────── */

const Body: FC<{ children: ReactNode; size?: number; maxWidth?: string }> = ({
  children,
  size = tokens.fontSize14,
  maxWidth,
}) => (
  <Typography
    sx={{
      fontFamily: tokens.fontBody,
      fontSize: `${size}px`,
      fontWeight: 400,
      lineHeight: 1.5,
      whiteSpace: "pre-line",
      overflowWrap: "anywhere",
      width: "100%",
      maxWidth,
    }}
  >
    {children}
  </Typography>
);

const Label: FC<{ children: ReactNode; color: string }> = ({
  children,
  color,
}) => (
  <Typography
    sx={{
      fontFamily: tokens.fontBody,
      fontSize: `${tokens.fontSize14}px`,
      fontWeight: 700,
      lineHeight: 1.5,
      color,
      width: "100%",
      overflowWrap: "anywhere",
    }}
  >
    {children}
  </Typography>
);

/**
 * Link inline sublinhado. O tema pinta `MuiLink` de `blue` com hover `cobalt` e
 * sublinhado em `alpha(blue, .4)` — o que desaparece sobre a superfície
 * `cobalt` do statement-viewer. Aqui a cor (texto **e** sublinhado) é sempre a
 * do texto ao redor, no repouso e no hover; o feedback de hover vem da
 * opacidade, que não depende de contraste com o fundo.
 */
const inlineLinkSx = {
  color: "inherit",
  textDecorationColor: "currentColor",
  "&:hover": {
    color: "inherit",
    textDecorationColor: "currentColor",
    opacity: 0.8,
  },
  "&:focus-visible": {
    outline: "2px solid currentColor",
    outlineOffset: "2px",
    borderRadius: tokens.radiusXs,
  },
} as const;

const InlineLink: FC<{ href?: string; children: ReactNode }> = ({
  href,
  children,
}) =>
  href ? (
    <Link
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      underline="always"
      sx={inlineLinkSx}
    >
      {children}
    </Link>
  ) : (
    <Box component="span" sx={{ textDecoration: "underline" }}>
      {children}
    </Box>
  );

/* ── Blocos ──────────────────────────────────────────────────────────────── */

const SOCIAL_GLYPHS = [
  { key: "instagram", label: "Instagram", Glyph: InstagramGlyph },
  { key: "linkedin", label: "LinkedIn", Glyph: LinkedinGlyph },
  { key: "youtube", label: "YouTube", Glyph: YoutubeGlyph },
] as const;

const SocialMedia: FC<{
  links: AppFooterProps["socialLinks"];
  color: string;
}> = ({ links, color }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      width: `${WORDMARK_WIDTH}px`,
      color,
    }}
  >
    {SOCIAL_GLYPHS.map(({ key, label, Glyph }) => {
      const href = links?.[key];
      return href ? (
        <Link
          key={key}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={label}
          sx={{
            color: "inherit",
            display: "flex",
            "&:hover": { color: "inherit", opacity: 0.8 },
            "&:focus-visible": {
              outline: "2px solid currentColor",
              outlineOffset: "2px",
              borderRadius: tokens.radiusXs,
            },
          }}
        >
          <Glyph size={SOCIAL_GLYPH} />
        </Link>
      ) : (
        <Box key={key} aria-label={label} role="img" sx={{ display: "flex" }}>
          <Glyph size={SOCIAL_GLYPH} />
        </Box>
      );
    })}
  </Box>
);

const ContactLine: FC<{
  glyph: ReactNode;
  children: ReactNode;
  nowrap: boolean;
}> = ({ glyph, children, nowrap }) => (
  <Box
    sx={{ display: "flex", alignItems: "center", gap: GAP_LINE, width: "100%" }}
  >
    <Box sx={{ display: "flex", flexShrink: 0 }}>{glyph}</Box>
    <Typography
      sx={{
        fontFamily: tokens.fontBody,
        fontSize: `${tokens.fontSize14}px`,
        fontWeight: 400,
        lineHeight: 1.5,
        // No desktop o contato não quebra: quem se ajusta é a coluna, que tem
        // 235px de mínimo e cresce com um e-mail longo. No mobile a largura é
        // a da tela, então quebrar é a única saída.
        whiteSpace: nowrap ? "nowrap" : "normal",
        overflowWrap: "anywhere",
      }}
    >
      {children}
    </Typography>
  </Box>
);

/**
 * Logo de parceiro como máscara CSS: a arte define o recorte e `tint` define a
 * cor. É o mesmo mecanismo do Figma (`mask-image` + fill), e é o que permite o
 * mesmo arquivo servir superfície escura e clara.
 */
const PartnerLogo: FC<{ logo: AppFooterPartnerLogo; tint: string }> = ({
  logo,
  tint,
}) => (
  <Box
    role="img"
    aria-label={logo.name}
    sx={{
      width: `${logo.width}px`,
      height: `${logo.height}px`,
      flexShrink: 0,
      backgroundColor: tint,
      maskImage: `url("${logo.src}")`,
      maskSize: "contain",
      maskRepeat: "no-repeat",
      maskPosition: "center",
      WebkitMaskImage: `url("${logo.src}")`,
      WebkitMaskSize: "contain",
      WebkitMaskRepeat: "no-repeat",
      WebkitMaskPosition: "center",
    }}
  />
);

/* ── Componente ──────────────────────────────────────────────────────────── */

/**
 * Footer das aplicações Área do Cliente (`statement-viewer`) e Visualizador de
 * Cobranças (`billing-viewer`), nas variações CUB e white label.
 *
 * Superfície por variante:
 * | app                | tenant       | fundo                          |
 * |--------------------|--------------|--------------------------------|
 * | `statement-viewer` | `cub`        | `cobalt`                       |
 * | `statement-viewer` | `whitelabel` | cor secundária do parceiro     |
 * | `billing-viewer`   | qualquer     | `grey200`                      |
 *
 * O tenant `"cub"` traz os dados da CUB como default (razão social, CNPJ,
 * contatos, endereço); o `"whitelabel"` espera tudo por prop e acrescenta a
 * régua + o selo "Tecnologia da CUB". Ver {@link AppFooterProps}.
 */
export const AppFooter: FC<AppFooterProps> = ({
  app,
  tenant = "cub",
  companyName,
  companyDocument,
  copyrightLine,
  copyrightYear = new Date().getFullYear(),
  companyLogoSrc,
  companyLogoRadius = PARTNER_LOGO_ART_RADIUS,
  companyText,
  showCompanyText = true,
  supportEmail,
  whatsapp,
  phone,
  address,
  availability,
  lateFeePercent,
  dailyInterestPercent,
  showLateCharges = true,
  socialLinks,
  showSocialMedia = true,
  privacyPolicyHref,
  billingValidatorHref,
  partnerColors,
  partners = CUB_PARTNERS,
  partnersTint,
  contentMaxWidth,
  contentGutter,
  mobileBreakpoint = "md",
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down(mobileBreakpoint));

  const isCub = tenant === "cub";
  const isStatement = app === "statement-viewer";
  const surface = resolveSurface({ app, tenant, partnerColors });
  // Alinhamento com o paper da página: `maxWidth` + centralização fazem o
  // trabalho, e o padding vira respiro MÍNIMO — para quando a viewport é mais
  // estreita que o max-width. O grid vem do DS, indexado pelo app; quem tiver
  // um paper diferente sobrescreve por prop.
  const grid = APP_CONTENT_GRID[app];
  const maxWidth = contentMaxWidth ?? `${grid.maxWidth}px`;
  const padX =
    contentGutter ?? `${isMobile ? grid.gutterMobile : grid.gutter}px`;

  /* ── Conteúdo resolvido (prop > default do tenant/app) ── */

  // Texto vazio/em branco conta como ausente: é o que chega de API e é o que
  // o controle do Storybook produz ao limpar o campo.
  const filled = (v?: string) => (v && v.trim() ? v : undefined);

  const name =
    filled(companyName) ?? (isCub ? CUB_TENANT_CONTENT.companyName : undefined);
  const doc =
    filled(companyDocument) ??
    (isCub ? CUB_TENANT_CONTENT.companyDocument : undefined);
  const email =
    filled(supportEmail) ??
    (isCub ? CUB_TENANT_CONTENT.supportEmail : undefined);
  const zap =
    filled(whatsapp) ?? (isCub ? CUB_TENANT_CONTENT.whatsapp : undefined);
  const tel =
    filled(phone) ??
    (isCub && !isStatement ? CUB_TENANT_CONTENT.phone : undefined);
  const addressText =
    filled(address) ??
    (isCub && isStatement ? CUB_TENANT_CONTENT.address : undefined);
  const availabilityText =
    filled(availability) ??
    (isCub && !isStatement ? CUB_TENANT_CONTENT.availability : undefined);

  // Redes: no tenant CUB são sempre as nossas; no white label, sempre as do parceiro.
  const resolvedSocialLinks = isCub
    ? CUB_TENANT_CONTENT.socialLinks
    : socialLinks;

  // O aviso de CIP + encargos descreve o boleto, não a marca — vale nos dois
  // tenants. O texto institucional é da CUB, então só o tenant "cub" tem default.
  const resolvedCompanyText = (() => {
    if (companyText != null) return companyText;
    if (isStatement) return isCub ? CUB_TENANT_CONTENT.companyText : undefined;
    return CIP_NOTICE;
  })();

  // Parágrafo próprio: `showCompanyText` não mexe nele, e vice-versa.
  const lateCharges =
    !isStatement && showLateCharges
      ? lateChargesSentence(lateFeePercent, dailyInterestPercent)
      : undefined;

  const privacy = PRIVACY_SENTENCE[app];
  const privacyHref =
    filled(privacyPolicyHref) ?? PLATFORM_LINKS.privacyPolicyHref;
  const validatorHref =
    filled(billingValidatorHref) ?? PLATFORM_LINKS.billingValidatorHref;

  /* ── Fragmentos compartilhados ── */

  const firstLine = copyrightLine ?? (
    <>
      {isCub
        ? `© Copyright ${copyrightYear} ${name} | CNPJ: ${doc}`
        : `${name} | CNPJ: ${doc}`}
      {isCub && (
        <>
          <br />
          {LABELS.allRightsReserved}
        </>
      )}
    </>
  );

  const privacySentence = (
    <>
      {privacy.before}
      <InlineLink href={privacyHref}>{LABELS.privacyPolicy}</InlineLink>
      {privacy.after}
    </>
  );

  const validatorSentence = (
    <>
      {LABELS.billingValidator}
      <InlineLink href={validatorHref}>
        {LABELS.billingValidatorLabel}
      </InlineLink>
    </>
  );

  const tenantBlock = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: GAP_BLOCK,
        flexShrink: 0,
      }}
    >
      {isCub ? (
        <Box sx={{ color: surface.wordmark, display: "flex" }}>
          <CubWordmark width={WORDMARK_WIDTH} />
        </Box>
      ) : (
        companyLogoSrc && (
          <Box
            component="img"
            src={companyLogoSrc}
            alt={name ?? ""}
            sx={{
              width: PARTNER_LOGO_SIZE,
              height: PARTNER_LOGO_SIZE,
              // Sem raio por default: a arte do parceiro traz a própria
              // moldura, e recortá-la come a borda colorida dela.
              borderRadius: companyLogoRadius,
              objectFit: "cover",
              display: "block",
            }}
          />
        )
      )}
      {/* Ícones de rede acompanham a cor de corpo, não a de rótulo: `info200` no
          statement CUB, `textSecondary` no billing, contraste do parceiro no
          white label (onde as duas coincidem). */}
      {showSocialMedia && (
        <SocialMedia links={resolvedSocialLinks} color={surface.body} />
      )}
    </Box>
  );

  const companyInfo = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: GAP_BLOCK,
        flex: "1 0 0",
        minWidth: isMobile ? 0 : TEXT_MIN_WIDTH,
      }}
    >
      {/* Bold, mas na cor de corpo — só rótulos e títulos usam `strong`. */}
      <Label color={surface.body}>{firstLine}</Label>
      {showCompanyText && resolvedCompanyText && (
        <Body>{resolvedCompanyText}</Body>
      )}
      {lateCharges && <Body>{lateCharges}</Body>}
      {/* No white label as frases de privacidade e do Validador migram para o
          bloco "Tecnologia da CUB", em 12px. */}
      {isCub && (
        <>
          <Body>{privacySentence}</Body>
          <Body>{validatorSentence}</Body>
        </>
      )}
    </Box>
  );

  const hasContacts = Boolean(email || zap || tel);
  const secondaryBlock = isStatement
    ? addressText && { label: LABELS.address, value: addressText }
    : availabilityText && {
        label: LABELS.availability,
        value: availabilityText,
      };

  const infoColumn = (hasContacts || secondaryBlock) && (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: GAP_BLOCK,
        flexShrink: 0,
        // A spec fixa 235px, mas um e-mail longo de parceiro não caberia. Aqui
        // 235px é o *mínimo* e a coluna cresce com o contato; endereço e
        // disponibilidade seguem quebrando em 235 (ver `maxWidth` abaixo).
        minWidth: isMobile ? "100%" : INFO_COLUMN_WIDTH,
        width: isMobile ? "100%" : "auto",
      }}
    >
      {hasContacts && (
        <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
          <Label color={surface.strong}>{LABELS.support}</Label>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: GAP_LINE,
              width: "100%",
            }}
          >
            {email && (
              <ContactLine
                glyph={
                  <Icon sx={{ fontSize: CONTACT_GLYPH }}>mail_outline</Icon>
                }
                nowrap={!isMobile}
              >
                {email}
              </ContactLine>
            )}
            {zap && (
              <ContactLine glyph={<WhatsappGlyph />} nowrap={!isMobile}>
                {zap}
              </ContactLine>
            )}
            {tel && (
              <ContactLine
                glyph={<Icon sx={{ fontSize: CONTACT_GLYPH }}>phone</Icon>}
                nowrap={!isMobile}
              >
                {tel}
              </ContactLine>
            )}
          </Box>
        </Box>
      )}
      {secondaryBlock && (
        <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
          <Label color={surface.strong}>{secondaryBlock.label}</Label>
          <Body maxWidth={isMobile ? undefined : INFO_COLUMN_WIDTH}>
            {secondaryBlock.value}
          </Body>
        </Box>
      )}
    </Box>
  );

  const textRow = (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        // `items-start` da spec: cada coluna tem a altura do próprio conteúdo,
        // então "Atendimento:" alinha com a primeira linha do texto da company.
        // Sem isso as colunas esticam na altura da linha e o bloco desce.
        alignItems: "flex-start",
        alignContent: "flex-start",
        columnGap: GAP_COLUMN,
        rowGap: GAP_SECTION,
        flex: "1 0 0",
        minWidth: 0,
        width: isMobile ? "100%" : undefined,
      }}
    >
      {companyInfo}
      {infoColumn}
    </Box>
  );

  const showPartners = isCub && isStatement && partners.length > 0;

  const partnersSection = showPartners && (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: GAP_BLOCK,
        width: "100%",
      }}
    >
      <Typography
        component="h2"
        sx={{
          fontFamily: tokens.fontDisplay,
          fontSize: `${tokens.fontSize20}px`,
          fontWeight: 700,
          lineHeight: 1.5,
          color: surface.strong,
          margin: 0,
        }}
      >
        {LABELS.partnersAndInvestors}
      </Typography>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: GAP_BLOCK,
        }}
      >
        {partners.map((logo) => (
          <PartnerLogo
            key={logo.name}
            logo={logo}
            tint={partnersTint ?? tokens.info200}
          />
        ))}
      </Box>
    </Box>
  );

  const poweredByCub = (
    <Box
      sx={{
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        flexWrap: "wrap",
        alignItems: isMobile ? "flex-start" : "center",
        alignContent: "center",
        columnGap: GAP_SECTION,
        rowGap: GAP_BLOCK,
        width: "100%",
      }}
    >
      {/* Selo fechado (moldura + lettering + wordmark) vindo do design em SVG;
          herda a cor do texto via `currentColor`. */}
      <Box sx={{ color: surface.strong, display: "flex", flexShrink: 0 }}>
        <PoweredByCubBadge />
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: GAP_SECURITY,
          flex: "1 0 0",
          minWidth: isMobile ? 0 : TEXT_MIN_WIDTH,
          width: isMobile ? "100%" : undefined,
        }}
      >
        <Body size={tokens.fontSize12}>{privacySentence}</Body>
        <Body size={tokens.fontSize12}>{validatorSentence}</Body>
      </Box>
    </Box>
  );

  // A faixa colorida sempre atravessa a viewport; quem posiciona o conteúdo é
  // o wrapper interno. Sem essa separação, um único padding tem de ser ao mesmo
  // tempo respiro absoluto e offset relativo do paper — e não pode ser os dois.
  const rootSx = {
    backgroundColor: surface.bg,
    color: surface.body,
    fontFamily: tokens.fontBody,
    paddingBlock: PAD_Y,
    overflow: "hidden",
    width: "100%",
  } as const;

  /** Caixa do conteúdo — a mesma regra que centraliza o paper da página. */
  const contentSx = {
    width: "100%",
    paddingInline: padX,
    maxWidth,
    marginInline: "auto",
  } as const;

  /* White label: coluna única com régua + selo da CUB na base. */
  if (!isCub) {
    return (
      <Box component="footer" sx={rootSx}>
        <Box
          sx={{
            ...contentSx,
            display: "flex",
            flexDirection: "column",
            gap: GAP_SECTION,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              alignItems: "flex-start",
              gap: isMobile ? GAP_SECTION : GAP_COLUMN,
              width: "100%",
            }}
          >
            {tenantBlock}
            {textRow}
          </Box>
          <Box
            sx={{
              width: "100%",
              height: "1px",
              backgroundColor: surface.divider,
              flexShrink: 0,
            }}
          />
          {poweredByCub}
        </Box>
      </Box>
    );
  }

  /* CUB: marca à esquerda, informação à direita. */
  return (
    <Box component="footer" sx={rootSx}>
      <Box
        sx={{
          ...contentSx,
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          flexWrap: "wrap",
          alignItems: "flex-start",
          alignContent: "flex-start",
          columnGap: GAP_COLUMN,
          rowGap: GAP_SECTION,
        }}
      >
        {tenantBlock}
        <Box
          sx={{
            // A spec aperta esse respiro no desktop (24px) e abre no mobile (44px).
            display: "flex",
            flexDirection: "column",
            gap: isMobile ? GAP_SECTION : GAP_BLOCK,
            flex: "1 0 0",
            minWidth: 0,
            width: isMobile ? "100%" : undefined,
          }}
        >
          {textRow}
          {partnersSection}
        </Box>
      </Box>
    </Box>
  );
};

export default AppFooter;
